import { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { PsyToolsId } from '../../components/meet/tools-panel/types';
import { DpDgVideoPanelClient } from '../../components/tools/dpdg/dpdg-video-client';
import { IPsyBlock } from '../../components/tools/new-questions/new-question-psy';
import { useAppSelector } from '../../hooks/redux';
import { DetectorEmotionClient } from '../../components/tools/detector-emo/detector-emo-client';
import { MacCardsClient } from '../../components/tools/mac-cards/mac-card-client';
import { LifeBalanceWheelClient } from '../../components/tools/life-balance-wheel/wheel-client';

import { Trajectory, normalizeTrajectory, useDpdgState } from '../../hooks/useDpdgState';
import { applyDpdgSyncAction, useSocketDpdg } from '../../hooks/useSocketDpdg';
import { ColorCodeClient } from '../../components/tools/color-code/color-code-client';
import { CloudsClient } from '../../components/tools/clouds/clouds-client';
import { Line } from '../../components/tools/clouds/types';
import { SandTherapyClient } from '../../components/tools/sand-therapy/sand-therapy-client';
import { Figure, PlacedFigure } from '../../components/tools/sand-therapy/types';

export interface IMeetToolsClientProps extends Omit<IPsyBlock, 'onSetMenu' | 'setResult'> {
  roomName: string;
  users: any[];
  /** Идентификатор участника в комнате — для привязки выбора МАК карт к конкретному клиенту */
  participantIdentity?: string;
  /** Групповая терапия: отправить выбор МАК карт психологу по data channel */
  sendMacSelectCards?: (result: Array<{ id: number; img: string; selected: boolean }>) => void;
  sendEmotionsSelect?: (result: { category: string[]; emo: string[] }) => void;
  sendWheelChange?: (values: number[]) => void;
  sendWheelResult?: (values: number[]) => void;
  wheelBootstrap?: {
    nonce: number;
    values: number[];
    sectorNames?: string[];
    isRealtimeVisible?: boolean;
    isSelectionUnlocked?: boolean;
  } | null;
  onWheelBootstrapConsumed?: () => void;
  sendColorCodeResult?: (colors: string[]) => void;
  onCloudsDrawLine?: (line: Line) => void;
  onCloudsDrawLines?: (lines: Line[]) => void;
  onCloudsClearDrawing?: () => void;
  onCloudsSelectImage?: (imageIndex: number) => void;
  onCloudsClientFinished?: () => void;
  onCloudsUndo?: (linesCount: number) => void;
  onCloudsHideVideoChange?: (hide: boolean) => void;
  onSandSelectFigures?: (figures: Figure[]) => void;
  onSandStartPlacement?: () => void;
  onSandPlaceFigure?: (figure: PlacedFigure) => void;
  onSandMoveFigure?: (id: string, x: number, y: number) => void;
  onSandRotateFigure?: (id: string) => void;
  onSandResizeFigure?: (id: string, scale: number) => void;
  onSandRemoveFigure?: (id: string) => void;
  onSandColorChange?: (color: string) => void;
  onSandClientReady?: () => void;
  dpdgBootstrap?: { nonce: number; initialSync?: Record<string, unknown> } | null;
  onDpdgBootstrapConsumed?: () => void;
  dpdgInbound?: { nonce: number; action: string; data: Record<string, unknown> } | null;
  onDpdgInboundConsumed?: () => void;
  sendDpdgMessage?: (action: string, data: Record<string, unknown>) => void;
  useDataChannelToolTransport?: boolean;
  sandPlacedFigures?: PlacedFigure[];
  sandColor?: string;
  sandClientReady?: boolean;
  /** Мобильный 1:1: поле ДПДГ на весь экран без скруглений */
  mobileLayout?: boolean;
}

export const MeetToolsClient: FC<IMeetToolsClientProps> = ({
  roomName,
  users,
  participantIdentity,
  sendMacSelectCards,
  sendEmotionsSelect,
  sendWheelChange,
  sendWheelResult,
  wheelBootstrap,
  onWheelBootstrapConsumed,
  sendColorCodeResult,
  onCloudsDrawLine,
  onCloudsDrawLines,
  onCloudsClearDrawing,
  onCloudsSelectImage,
  onCloudsClientFinished,
  onCloudsUndo,
  onCloudsHideVideoChange,
  onSandSelectFigures,
  onSandStartPlacement,
  onSandPlaceFigure,
  onSandMoveFigure,
  onSandRotateFigure,
  onSandResizeFigure,
  onSandRemoveFigure,
  onSandColorChange,
  onSandClientReady,
  dpdgBootstrap,
  onDpdgBootstrapConsumed,
  dpdgInbound,
  onDpdgInboundConsumed,
  sendDpdgMessage,
  useDataChannelToolTransport,
  sandPlacedFigures = [],
  sandColor = '#fef3c7',
  sandClientReady = false,
  mobileLayout = false,
}) => {
  const settings = useAppSelector((state) => state.appSettingsSlice.settings);
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);

  const mapSliderToVelocity = (val: number): number => {
    const minV = 0.05;
    const maxV = 4;

    return ((val - 1) / 39) * (maxV - minV) + minV;
  };

  const {
    trajectory,
    setTrajectory,
    bgColor,
    setBgColor,
    ballColor,
    setBallColor,
    velocity,
    setVelocity,
    backgroundType,
    setBackgroundType,
    backgroundValue,
    setBackgroundValue,
    ballSize,
    setBallSize,
  } = useDpdgState({
    initialSpeed: settings?.defaults_dpdg_speed ? mapSliderToVelocity(Number(settings.defaults_dpdg_speed)) : 0.2,
    initialSize: settings?.defaults_dpdg_size ? Number(settings.defaults_dpdg_size) : 30,
    initialBackground: settings?.defaults_dpdg_background || '',
    initialTrajectory: 'horizontal',
  });

  const [dpdgBallMovingRemote, setDpdgBallMovingRemote] = useState<boolean | undefined>(undefined);

  const applyDpdgPatch = useMemo(
    () =>
      (data: {
        trajectory?: Trajectory;
        bgColor?: string;
        ballColor?: string;
        velocity?: number;
        backgroundType?: 'none' | 'video' | 'image';
        backgroundValue?: string;
        ballSize?: number;
        moving?: boolean;
      }) => {
        if (data.trajectory) setTrajectory(normalizeTrajectory(data.trajectory));
        if (data.bgColor) setBgColor(data.bgColor);
        if (data.ballColor) setBallColor(data.ballColor);
        if (data.velocity !== undefined) setVelocity(data.velocity);
        if (data.backgroundType !== undefined && data.backgroundType !== null) {
          setBackgroundType(data.backgroundType);
          setBackgroundValue(data.backgroundValue ?? '');
        }
        if (data.ballSize !== undefined) setBallSize(data.ballSize);
        if (data.moving !== undefined) setDpdgBallMovingRemote(data.moving);
      },
    [setTrajectory, setBgColor, setBallColor, setVelocity, setBackgroundType, setBackgroundValue, setBallSize],
  );

  useSocketDpdg(false, roomName, applyDpdgPatch, {
    socketDisabled: true,
  });

  useEffect(() => {
    if (!dpdgBootstrap) return;
    const sync = dpdgBootstrap.initialSync;
    if (sync && typeof sync === 'object') {
      applyDpdgPatch({
        trajectory: normalizeTrajectory((sync.trajectory as Trajectory) || 'horizontal'),
        bgColor: (sync.bgColor as string) || '#F3F4F6',
        ballColor: (sync.ballColor as string) || '#3b65b9',
        velocity: typeof sync.velocity === 'number' ? sync.velocity : 0.2,
        backgroundType: (sync.backgroundType as 'none' | 'video' | 'image') || 'none',
        backgroundValue: (sync.backgroundValue as string) || '',
        ballSize: typeof sync.ballSize === 'number' ? sync.ballSize : 30,
      });
    }
    onDpdgBootstrapConsumed?.();
  }, [dpdgBootstrap, applyDpdgPatch, onDpdgBootstrapConsumed]);

  useEffect(() => {
    if (!dpdgInbound) return;
    applyDpdgSyncAction(dpdgInbound.action, dpdgInbound.data, applyDpdgPatch);
    onDpdgInboundConsumed?.();
  }, [dpdgInbound, applyDpdgPatch, onDpdgInboundConsumed]);

  const safeTrajectory = normalizeTrajectory(trajectory);

  const tool = useMemo(() => {
    switch (activeTool) {
      case PsyToolsId.EMOTIONS:
        return <DetectorEmotionClient mobile={mobileLayout} sendEmotionsSelect={sendEmotionsSelect} />;

      case PsyToolsId.WHEEL:
        return (
          <LifeBalanceWheelClient
            mobile={mobileLayout}
            sendWheelChange={sendWheelChange}
            sendWheelResult={sendWheelResult}
            wheelBootstrap={wheelBootstrap}
            onWheelBootstrapConsumed={onWheelBootstrapConsumed}
          />
        );

      case PsyToolsId.MAC_CARDS:
        return (
          <MacCardsClient
            mobile={mobileLayout}
            participantIdentity={participantIdentity}
            sendMacSelectCards={sendMacSelectCards}
          />
        );

      case PsyToolsId.DPDG:
        return (
          <DpDgVideoPanelClient
            mobile={mobileLayout}
            trajectory={safeTrajectory}
            roomName={roomName}
            bgColor={bgColor}
            ballColor={ballColor}
            velocity={velocity}
            backgroundType={backgroundType}
            backgroundValue={backgroundValue}
            ballSize={ballSize}
            sendDpdgMessage={sendDpdgMessage}
            useDataChannelToolTransport
            liveKitBallMoving={dpdgBallMovingRemote}
          />
        );

      case PsyToolsId.COLOR_CODE:
        return <ColorCodeClient mobile={mobileLayout} sendColorCodeResult={sendColorCodeResult} />;

      case PsyToolsId.CLOUDS:
        return (
          <CloudsClient
            mobile={mobileLayout}
            onDrawLine={onCloudsDrawLine}
            onDrawLines={onCloudsDrawLines}
            useDataChannelTransport={Boolean(useDataChannelToolTransport)}
            onClearDrawing={onCloudsClearDrawing}
            onSelectImage={onCloudsSelectImage}
            onClientFinished={onCloudsClientFinished}
            onUndo={onCloudsUndo}
            onHideVideoChange={onCloudsHideVideoChange}
          />
        );

      case PsyToolsId.SAND_THERAPY:
        return (
          <SandTherapyClient
            mobile={mobileLayout}
            onSelectFigures={onSandSelectFigures}
            onStartPlacement={onSandStartPlacement}
            onPlaceFigure={onSandPlaceFigure}
            onMoveFigure={onSandMoveFigure}
            onRotateFigure={onSandRotateFigure}
            onResizeFigure={onSandResizeFigure}
            onRemoveFigure={onSandRemoveFigure}
            onSandColorChange={onSandColorChange}
            onClientReady={onSandClientReady}
            placedFigures={sandPlacedFigures}
            sandColor={sandColor}
            clientReady={sandClientReady}
          />
        );

      default:
        return null;
    }
  }, [
    activeTool,
    roomName,
    safeTrajectory,
    bgColor,
    ballColor,
    velocity,
    backgroundType,
    backgroundValue,
    ballSize,
    onCloudsDrawLine,
    onCloudsDrawLines,
    onCloudsClearDrawing,
    onCloudsSelectImage,
    onCloudsClientFinished,
    onCloudsUndo,
    onCloudsHideVideoChange,
    sendEmotionsSelect,
    sendWheelChange,
    sendWheelResult,
    wheelBootstrap,
    onWheelBootstrapConsumed,
    sendColorCodeResult,
    sendDpdgMessage,
    useDataChannelToolTransport,
    dpdgBallMovingRemote,
    mobileLayout,
    onSandSelectFigures,
    onSandStartPlacement,
    onSandPlaceFigure,
    onSandMoveFigure,
    onSandRotateFigure,
    onSandResizeFigure,
    onSandRemoveFigure,
    onSandColorChange,
    onSandClientReady,
    sandPlacedFigures,
    sandColor,
    sandClientReady,
  ]);

  if (!tool) return null;

  return (
    <div
      className={classNames('h-full overflow-hidden', mobileLayout ? 'rounded-none' : 'rounded-chip md:rounded-card')}
    >
      {tool}
    </div>
  );
};
